import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const CartCtx = createContext(null);

export function CartProvider({ children }) {
    const [items, setItems] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('rrm_cart') || '[]');
        } catch {
            return [];
        }
    });

    useEffect(() => {
        localStorage.setItem('rrm_cart', JSON.stringify(items));
    }, [items]);

    const add = useCallback((product, qty = 1) => {
        setItems((prev) => {
            const existing = prev.find((p) => p.product_id === product.id);
            if (existing) {
                return prev.map((p) => (p.product_id === product.id ? { ...p, quantity: Math.min(p.quantity + qty, 99) } : p));
            }
            return [
                ...prev,
                {
                    product_id: product.id,
                    name: product.name,
                    price: product.price,
                    image_url: product.image_url,
                    sku: product.sku,
                    quantity: qty,
                },
            ];
        });
    }, []);

    const remove = useCallback((productId) => setItems((prev) => prev.filter((p) => p.product_id !== productId)), []);
    const updateQty = useCallback(
        (productId, qty) =>
            setItems((prev) => prev.map((p) => (p.product_id === productId ? { ...p, quantity: Math.max(1, Math.min(qty, 99)) } : p))),
        []
    );
    const clear = useCallback(() => setItems([]), []);

    const totalCount = useMemo(() => items.reduce((s, p) => s + p.quantity, 0), [items]);
    const totalAmount = useMemo(() => items.reduce((s, p) => s + p.quantity * Number(p.price || 0), 0), [items]);

    const value = useMemo(
        () => ({ items, add, remove, updateQty, clear, totalCount, totalAmount }),
        [items, add, remove, updateQty, clear, totalCount, totalAmount]
    );

    return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => useContext(CartCtx);
